import React from 'react';
import { Link } from 'react-router-dom';
import { Typography, Row, Col, Card, Space, Timeline, Divider, Image } from 'antd';
import {
  DatabaseOutlined,
  ToolOutlined,
  TeamOutlined,
  CalendarOutlined,
  LineChartOutlined,
  FileTextOutlined,
  FolderOpenOutlined,
  CheckCircleOutlined,
  SafetyOutlined,
  CloudServerOutlined,
  LockOutlined,
  CustomerServiceOutlined,
} from '@ant-design/icons';
import { PublicPageLayout } from '../../components/public/PublicPageLayout';
import { SEOHead } from '../../components/SEOHead';
import '../../styles/service-detail.css';

const { Title, Paragraph, Text } = Typography;

// セクション1: サービス概要
const ServiceOverviewSection: React.FC = () => {
  return (
    <section className="service-overview-section">
      <div className="overview-background">
        <img
          src="/images/landing/dx-tablet.jpg"
          alt="タブレットで設備管理"
          className="overview-bg-image"
        />
        <div className="overview-overlay"></div>
      </div>
      <div className="section-container">
        <Row gutter={[48, 48]} align="middle" justify="center">
          <Col xs={24} lg={18} xl={16}>
            <div className="overview-content-center">
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div className="service-label">一元帳について</div>
                <Title level={1} style={{ color: 'white', textAlign: 'center' }}>
                  設備管理を、シンプルに。
                </Title>
                <Paragraph className="lead-text" style={{ color: 'rgba(255,255,255,0.9)', textAlign: 'center' }}>
                  設備管理の「情報分散」「属人化」「突発対応」を解決する、
                  中小製造業向けクラウド設備管理システムです。
                </Paragraph>
                <Card className="concept-card">
                  <Title level={4}>コンセプト</Title>
                  <Paragraph style={{ margin: 0 }}>
                    設備・部品・消耗品・業者・作業履歴・書類を1つのシステムで一元管理。
                    チーム全員が情報にアクセスでき、データに基づく計画的メンテナンスを実現します。
                  </Paragraph>
                </Card>
              </Space>
            </div>
          </Col>
        </Row>

        <Divider style={{ margin: '64px 0', borderColor: 'rgba(255,255,255,0.2)' }} />

        <Row gutter={[24, 24]}>
          <Col xs={24} sm={12} md={6}>
            <Card className="feature-highlight-card feature-highlight-card--white">
              <DatabaseOutlined style={{ fontSize: 40, color: '#1677ff', marginBottom: 16 }} />
              <Title level={4}>一元管理</Title>
              <Text>設備・部品・消耗品・業者・書類情報をまとめて管理</Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card className="feature-highlight-card feature-highlight-card--white">
              <TeamOutlined style={{ fontSize: 40, color: '#1677ff', marginBottom: 16 }} />
              <Title level={4}>無制限ユーザー</Title>
              <Text>チーム全員で使える(全プラン共通)</Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card className="feature-highlight-card feature-highlight-card--white">
              <LineChartOutlined style={{ fontSize: 40, color: '#1677ff', marginBottom: 16 }} />
              <Title level={4}>健全度予測</Title>
              <Text>「見えない劣化」を数値化</Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card className="feature-highlight-card feature-highlight-card--white">
              <CalendarOutlined style={{ fontSize: 40, color: '#1677ff', marginBottom: 16 }} />
              <Title level={4}>保全カレンダー</Title>
              <Text>予定・実績を可視化</Text>
            </Card>
          </Col>
        </Row>
      </div>
    </section>
  );
};

// セクション2: 主要機能詳細
const FeaturesDetailSection: React.FC = () => {
  const features = [
    {
      icon: <DatabaseOutlined />,
      title: '設備台帳管理',
      description: 'すべての設備情報を1箇所で管理',
      details: [
        {
          subtitle: '基本機能',
          items: [
            '設備名、型式、メーカー、購入日、エリア（設置場所）などの基本情報管理',
            '写真・図面・マニュアルの添付',
            '工場独自の管理項目を追加可能（カスタム項目）',
            '条件指定での瞬時絞り込み検索',
          ],
        },
        {
          subtitle: '活用例',
          items: [
            '検索機能で設備情報と過去の作業履歴がすぐ表示',
            '「この設備、いつメンテナンスした?」が3秒で分かる',
          ],
        },
      ],
      screenshots: [
        { src: '/images/screenshots/equipment-1.jpg', alt: '設備一覧画面' },
        { src: '/images/screenshots/equipment-2.jpg', alt: '設備詳細画面' },
      ],
    },
    {
      icon: <ToolOutlined />,
      title: '部品・消耗品管理',
      badge: 'NEW',
      description: '部品・消耗品の在庫・発注・交換履歴を一元管理',
      details: [
        {
          subtitle: '基本機能',
          items: [
            '部品・消耗品ごとの在庫数、保管場所（エリア）の管理',
            '発注履歴、納期、コスト記録',
            'いつ、どの設備に使用したか記録',
            '在庫が一定数を下回ったら通知',
            '設備ごとに使用部品・消耗品を確認',
          ],
        },
        {
          subtitle: '活用例',
          items: [
            '「この部品・消耗品、在庫あったっけ?」→部品・消耗品検索ですぐに確認',
            '在庫切れなら発注履歴から再発注も簡単',
          ],
        },
      ],
      screenshots: [
        { src: '/images/screenshots/parts-list.jpg', alt: '部品・消耗品一覧画面' },
      ],
    },
    {
      icon: <TeamOutlined />,
      title: '業者管理',
      badge: 'NEW',
      description: '協力会社の情報と作業実績を一元管理',
      details: [
        {
          subtitle: '基本機能',
          items: [
            '業者情報: 社名、連絡先、得意分野の登録',
            '作業実績: 過去の作業内容、対応設備の記録',
            '実績分析: 業者別の作業件数、コスト集計',
            '連絡先管理: 担当者情報の一元管理',
          ],
        },
        {
          subtitle: '活用例',
          items: [
            '「この設備の修理、どこに頼めばいい?」→業者管理で過去の実績を確認',
            '適切な業者を選定して連絡',
          ],
        },
      ],
      screenshots: [
        { src: '/images/screenshots/suppliers-list.jpg', alt: '業者一覧画面' },
      ],
    },
    {
      icon: <FolderOpenOutlined />,
      title: '書類台帳・法定点検管理',
      badge: 'NEW',
      description: '書類と提出履歴を関連先に紐付けて管理',
      details: [
        {
          subtitle: '基本機能',
          items: [
            '書類カテゴリごとの管理（一般 / 法定点検）',
            'PDF / Word / Excel / PowerPoint を登録',
            'エリア・設備・部品・業者・ユーザーへの関連付け',
            '改版履歴と変更メモの記録',
            '書類名・説明・ファイル名で検索',
          ],
        },
        {
          subtitle: '法定点検管理',
          items: [
            'ビル単位で提出状況を一覧表示',
            '提出・承認・差戻しの履歴管理',
            '次回期限と期限超過の可視化',
          ],
        },
        {
          subtitle: '活用例',
          items: [
            '監査前に提出書類の最新版を即時確認',
            '拠点ごとの法定点検の抜けを一目で把握',
          ],
        },
      ],
    },
    {
      icon: <CalendarOutlined />,
      title: '保全カレンダー',
      description: 'メンテナンス予定と実績を可視化',
      details: [
        {
          subtitle: '基本機能',
          items: [
            '保全カレンダー表示: 月次/週次で作業予定を確認',
            '担当者アサイン: 誰が、いつ、何をするか一目瞭然',
            '作業実績記録: 完了後、実績と写真を記録',
            'リマインド通知: 作業予定日の前日に自動通知',
            '進捗管理: 予定と実績の差分を可視化',
          ],
        },
        {
          subtitle: '活用例',
          items: [
            '「今月のメンテナンス予定は?」→保全カレンダーで一覧表示',
            '担当者と作業内容がすぐ分かる',
          ],
        },
      ],
      screenshots: [
        { src: '/images/screenshots/equipment-3.jpg', alt: '保全カレンダー画面' },
      ],
    },
    {
      icon: <LineChartOutlined />,
      title: '健全度予測',
      badge: 'NEW',
      description: '業界に合わせた2つのモードで設備を管理',
      details: [
        {
          subtitle: '2つの管理モード',
          items: [
            '健全度モード（工場・製造業向け）: ワイブル分布による詳細な健全度予測。環境係数やメンテナンス状況を反映した高度な計算',
            '耐用年数モード（病院・ホテル等向け）: メーカー耐用年数を基準にしたシンプルな管理。耐用年数超過を一目で確認',
            '業界別の自動設定: 新規登録時に業界を選択すると、最適なモードが自動設定されます',
            'いつでも切り替え可能: 設定画面で管理方式をいつでも変更できます',
          ],
        },
        {
          subtitle: '健全度モードの機能',
          items: [
            '劣化度スコア: 設備の健全度を0-100%で表示',
            '予測アルゴリズム: 使用年数、稼働時間、環境係数から算出',
            '更新提案: 劣化度に応じた最適な更新時期を提案',
            'リスク評価: 故障リスクの高い設備を優先表示',
            '詳細なグラフ表示: ワイブル分布曲線と影響要因分析',
          ],
        },
        {
          subtitle: '耐用年数モードの機能',
          items: [
            'シンプルな一覧表示: 設備名、耐用年数、経過年数、残存年数、ステータスを表示',
            '自動ステータス判定: 要交換/交換検討/良好を自動表示',
            'フィルタリング: ステータス別、カテゴリ別の絞り込み',
            '統計ダッシュボード: 要交換設備数、交換検討設備数を一目で確認',
          ],
        },
        {
          subtitle: '活用例',
          items: [
            '「この設備、そろそろ更新すべき?」→健全度予測で劣化度70%を確認',
            '「耐用年数を超えた設備は?」→耐用年数モードで一覧表示',
            'データに基づく計画的な設備更新',
          ],
        },
      ],
      screenshots: [
        { src: '/images/screenshots/deterioration-list.jpg', alt: '健全度予測一覧画面' },
        { src: '/images/screenshots/deterioration-detail.jpg', alt: '劣化度詳細画面' },
      ],
    },
    {
      icon: <FileTextOutlined />,
      title: '作業指示書・履歴管理',
      description: '作業内容の標準化とナレッジ蓄積',
      details: [
        {
          subtitle: '基本機能',
          items: [
            'テンプレート: 作業内容をテンプレート化',
            '指示書発行: チェックリスト付き作業指示書を発行',
            '実績記録: 作業内容、所要時間、写真を記録',
            '履歴検索: 過去の作業内容を瞬時に検索',
            'ナレッジ化: ベテランのノウハウをデジタル化',
          ],
        },
        {
          subtitle: '活用例',
          items: [
            '「新人に作業を教える時間がない」→作業指示書のテンプレートを見せるだけ',
            '手順とポイントが明確',
          ],
        },
      ],
      screenshots: [
        { src: '/images/screenshots/work-order-templates.jpg', alt: '作業指示書テンプレート' },
        { src: '/images/screenshots/work-history.jpg', alt: '作業履歴一覧' },
      ],
    },
  ];

  return (
    <section className="features-detail-section">
      <div className="section-container">
        <div className="section-header">
          <Title level={2}>主要機能の詳細</Title>
          <Paragraph className="section-description">
            設備管理に必要なすべての機能を詳しくご紹介
          </Paragraph>
        </div>

        {features.map((feature, index) => (
          <div key={index} className="feature-detail-block">
            <Row gutter={[48, 48]} align="middle">
              <Col xs={24} lg={feature.screenshots ? 14 : 24}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <div className="feature-detail-header">
                    <div className="feature-detail-icon">{feature.icon}</div>
                    <div>
                      <Space>
                        <Title level={3} style={{ margin: 0 }}>{feature.title}</Title>
                        {feature.badge && (
                          <span className={`detail-badge ${feature.badge === 'NEW' ? 'badge-new' : 'badge-option'}`}>
                            {feature.badge}
                          </span>
                        )}
                      </Space>
                      <Paragraph className="feature-detail-description">
                        {feature.description}
                      </Paragraph>
                    </div>
                  </div>

                  {feature.details.map((detail, idx) => (
                    <div key={idx}>
                      <Title level={5}>{detail.subtitle}</Title>
                      <ul className="feature-detail-list">
                        {detail.items.map((item, i) => (
                          <li key={i}>
                            <CheckCircleOutlined style={{ color: '#52c41a' }} />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </Space>
              </Col>
              {feature.screenshots && feature.screenshots.length > 0 && (
                <Col xs={24} lg={10}>
                  <div className="feature-screenshots-wrapper">
                    <Text strong style={{ display: 'block', marginBottom: 12, fontSize: 14 }}>
                      画面イメージ（クリックで拡大）
                    </Text>
                    <Image.PreviewGroup>
                      <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        {feature.screenshots.map((screenshot, idx) => (
                          <div key={idx} className="screenshot-item">
                            <Image
                              src={screenshot.src}
                              alt={screenshot.alt}
                              className="screenshot-thumbnail"
                              preview={{
                                mask: (
                                  <div style={{ fontSize: 14 }}>
                                    クリックして拡大
                                  </div>
                                ),
                              }}
                            />
                            <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                              {screenshot.alt}
                            </Text>
                          </div>
                        ))}
                      </Space>
                    </Image.PreviewGroup>
                  </div>
                </Col>
              )}
            </Row>
            {index < features.length - 1 && <Divider style={{ margin: '64px 0' }} />}
          </div>
        ))}
      </div>
    </section>
  );
};

// セクション3: 活用シーン
const UseCasesSection: React.FC = () => {
  const useCases = [
    {
      title: '日常のメンテナンス業務',
      timeline: [
        { label: '朝', content: '保全カレンダーで今日の作業を確認 → 担当者と作業内容を把握' },
        { label: '現場', content: '設備情報にアクセス → 過去の作業履歴と注意点を確認' },
        { label: '作業後', content: '実績記録と写真を登録 → チーム全体に作業完了を共有' },
        { label: '夕方', content: '次週の作業予定を調整 → 部品・消耗品在庫を確認し、必要なら発注' },
      ],
    },
    {
      title: '突発故障への対応',
      timeline: [
        { label: '故障発生', content: '設備情報から過去のトラブル履歴を確認 → 同様のケースがあれば対処法を参照' },
        { label: '応急処置', content: '作業内容を記録 → 後で詳細分析するための情報を残す' },
        { label: '業者手配', content: '業者管理から最適な協力会社を選定 → 過去の実績と評価を参考に依頼' },
        { label: '復旧後', content: '原因と対策を履歴に記録 → 次回以降の予防保全に活用' },
      ],
    },
    {
      title: '設備更新の判断',
      timeline: [
        { label: '現状分析', content: '健全度予測で劣化度を確認 → 複数設備の劣化状況を比較' },
        { label: 'コスト試算', content: '過去の修繕費用を集計 → 更新と延命のコスト比較' },
        { label: '優先順位', content: 'リスク評価で更新順位を決定 → 重要設備から計画的に更新' },
        { label: '予算申請', content: 'データに基づく根拠資料を作成 → 経営層への説明がスムーズに' },
      ],
    },
    {
      title: '新メンバーの受け入れ',
      timeline: [
        { label: '初日', content: 'アカウント作成(追加料金なし) → 全機能にすぐアクセス可能' },
        { label: '1週目', content: '設備台帳と作業履歴を閲覧 → 設備の特性と過去のトラブルを学習' },
        { label: '2週目', content: '作業指示書を見ながら実務開始 → 手順とポイントが明確で迷わない' },
        { label: '1ヶ月後', content: 'ベテランのサポート量が減少 → 自立して作業できるように' },
      ],
    },
  ];

  return (
    <section className="use-cases-section">
      <div className="section-container">
        <div className="section-header">
          <Title level={2}>活用シーン</Title>
          <Paragraph className="section-description">
            実際の業務での使い方をご紹介
          </Paragraph>
        </div>

        <Row gutter={[32, 32]}>
          {useCases.map((useCase, index) => (
            <Col xs={24} lg={12} key={index}>
              <Card className="use-case-card">
                <Title level={4}>{useCase.title}</Title>
                <Timeline
                  items={useCase.timeline.map((item) => ({
                    color: '#1677ff',
                    dot: <CheckCircleOutlined style={{ fontSize: 16 }} />,
                    children: (
                      <div>
                        <Text strong>{item.label}</Text>
                        <br />
                        <Text type="secondary">{item.content}</Text>
                      </div>
                    ),
                  }))}
                />
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    </section>
  );
};

// セクション4: 導入メリット
const BenefitsSection: React.FC = () => {
  const benefits = [
    {
      icon: <DatabaseOutlined />,
      title: '情報検索時間を90%削減',
      points: [
        'バラバラな情報を1つに集約',
        '必要な情報に数秒でアクセス',
        '「誰に聞けばいいか分からない」を解消',
      ],
    },
    {
      icon: <TeamOutlined />,
      title: '属人化を解消',
      points: [
        '情報をデジタル化してチーム全体で共有',
        'ベテラン不在時も対応可能に',
        '新人教育の時間を短縮',
      ],
    },
    {
      icon: <SafetyOutlined />,
      title: '計画的メンテナンスで突発対応を削減',
      points: [
        '健全度予測で優先順位を明確化',
        '予防保全で突発故障を削減',
        '保守費の最適化',
      ],
    },
    {
      icon: <TeamOutlined />,
      title: 'チーム全員で使える',
      points: [
        'ユーザー数無制限(全プラン共通)',
        '追加メンバーも追加料金なし',
        '現場スタッフから管理職まで、全員がアクセス可能',
      ],
    },
    {
      icon: <LineChartOutlined />,
      title: '柔軟な拡張性',
      points: [
        '設備数・予測枠は追加オプションで追加可能',
        '事業拡大に応じてプランをアップグレード',
        '小さく始めて大きく育てられる',
      ],
    },
  ];

  return (
    <section className="benefits-section">
      <div className="section-container">
        <div className="section-header">
          <Title level={2}>導入メリット</Title>
          <Paragraph className="section-description">
            一元帳が現場にもたらす5つのメリット
          </Paragraph>
        </div>

        <Row gutter={[24, 24]}>
          {benefits.map((benefit, index) => (
            <Col xs={24} md={12} lg={8} key={index}>
              <Card className="benefit-card">
                <div className="benefit-icon">{benefit.icon}</div>
                <Title level={4}>{benefit.title}</Title>
                <ul className="benefit-points">
                  {benefit.points.map((point, idx) => (
                    <li key={idx}>
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    </section>
  );
};

// セクション5: セキュリティ・信頼性
const SecuritySection: React.FC = () => {
  const securityFeatures = [
    {
      icon: <LockOutlined />,
      title: 'データ保護',
      items: [
        'SSL/TLS暗号化通信',
        'データベース暗号化',
        '定期バックアップ(自動)',
        'データセンター: AWS東京・シンガポールリージョン',
      ],
    },
    {
      icon: <SafetyOutlined />,
      title: 'アクセス制御',
      items: [
        'ロールベース権限管理',
        'パスワードポリシー設定',
        'アカウントロックアウト機能',
        'セッション管理',
      ],
    },
    {
      icon: <FileTextOutlined />,
      title: '監査・コンプライアンス',
      items: [
        '操作ログの記録',
        'データエクスポート機能',
        '個人情報保護方針に準拠',
        '定期的なセキュリティ監査',
      ],
    },
    {
      icon: <CloudServerOutlined />,
      title: '可用性',
      items: [
        '高い稼働率を目標としたシステム設計',
        '冗長化構成',
        '障害監視システム',
        '定期的なシステムメンテナンス',
      ],
    },
  ];

  return (
    <section className="security-section">
      <div className="section-container">
        <div className="section-header">
          <Title level={2}>セキュリティ・信頼性</Title>
          <Paragraph className="section-description">
            安心してご利用いただくための万全の体制
          </Paragraph>
        </div>

        <Row gutter={[24, 24]}>
          {securityFeatures.map((feature, index) => (
            <Col xs={24} md={12} key={index}>
              <Card className="security-card">
                <div className="security-header">
                  <div className="security-icon">{feature.icon}</div>
                  <Title level={4}>{feature.title}</Title>
                </div>
                <ul className="security-list">
                  {feature.items.map((item, idx) => (
                    <li key={idx}>
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    </section>
  );
};

// セクション6: サポート体制
const SupportSection: React.FC = () => {
  return (
    <section className="support-section">
      <div className="section-container-narrow">
        <div className="section-header">
          <Title level={2}>サポート体制</Title>
          <Paragraph className="section-description">
            導入から運用まで、お問い合わせ対応
          </Paragraph>
        </div>

        <Row gutter={[24, 24]} justify="center">
          <Col xs={24} md={16}>
            <Card className="support-card">
              <CustomerServiceOutlined style={{ fontSize: 40, color: '#1677ff', marginBottom: 16 }} />
              <Title level={4}>基本サポート</Title>
              <ul className="support-list">
                <li>メールサポート対応</li>
                <li>FAQサイトの提供</li>
                <li>操作マニュアルの提供</li>
                <li>システムアップデート情報の通知</li>
              </ul>
            </Card>
          </Col>
        </Row>
      </div>
    </section>
  );
};

// セクション7: CTA
const DetailCTASection: React.FC = () => {
  return (
    <section className="detail-cta-section">
      <div className="section-container-narrow">
        <Card className="detail-cta-card">
          <Space direction="vertical" size="large" style={{ width: '100%', textAlign: 'center' }}>
            <Title level={2} style={{ margin: 0 }}>
              まずは無料登録で<br />
              すべての機能をお試しください
            </Title>
            <Paragraph style={{ fontSize: 16, marginBottom: 24 }}>
              クレジットカード登録不要で全機能(数量制限あり)をご利用いただけます
            </Paragraph>
            <Space size="middle" wrap style={{ justifyContent: 'center' }}>
              <Link
                className="public-btn public-btn--primary public-btn--lg"
                to="/register"
              >
                無料登録を開始
              </Link>
              <Link
                className="public-btn public-btn--brand public-btn--lg"
                to="/service/plans"
              >
                料金プランを見る
              </Link>
            </Space>
          </Space>
        </Card>
      </div>
    </section>
  );
};

// メインコンポーネント
export const ServiceDetailPage: React.FC = () => {
  return (
    <>
      <SEOHead
        title="一元帳の特長｜設備管理ソフトとしてできること"
        description="一元帳は設備・部品・業者・作業履歴をまとめて管理できる設備管理ソフト。現場の情報分散や属人化を防ぎ、チーム全体での設備管理を実現します。"
      />
      <PublicPageLayout>
        <div className="service-detail-page">
        <ServiceOverviewSection />
        <FeaturesDetailSection />
        <UseCasesSection />
        <BenefitsSection />
        <SecuritySection />
        <SupportSection />
        <DetailCTASection />
      </div>
    </PublicPageLayout>
    </>
  );
};
